import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { RatesStatuses } from './rates-statuses';
import { RejectedChecksDialog } from './rejected-checks-dialog';
import { HourChangeRequestsDialog } from './dialogs/hour-change-requests-dialog';
import { HighOverviewReportTypeRes } from '../../../../../api/types/reports/high-overview/high-overview.type';

export enum FinancialMetricType {
  PENDING_RATES = 'PENDING_RATES',
  RETURNED_CHECKS = 'RETURNED_CHECKS',
  PENDING_HOUR_CHANGE_REQUESTS = 'PENDING_HOUR_CHANGE_REQUESTS',
  MONTHLY_HOURS = 'MONTHLY_HOURS',
  AVERAGE_HOURLY_RATE = 'AVERAGE_HOURLY_RATE',
  TOTAL_TEACHING_EXPENSES = 'TOTAL_TEACHING_EXPENSES',
}

interface FinancialMetric {
  type: FinancialMetricType;
  label: string;
  value: number;
  icon: string;
}

export interface HighLevelReportFinancialMeasuresDatamodel {
  data: HighOverviewReportTypeRes | undefined;
  onDataRefresh?: () => void;
}

export const HighLevelReportFinancialMeasures: React.FC<
  HighLevelReportFinancialMeasuresDatamodel
> = ({ data, onDataRefresh }) => {
  const [financialMetrics, setFinancialMetrics] = useState<FinancialMetric[]>(
    [],
  );

  useEffect(() => {
    setFinancialMetrics([
      {
        type: FinancialMetricType.PENDING_RATES,
        label: 'מספר תעריפים הממתינים לאישור',
        value: data?.financialMeasures.pendingRates.length ?? 0,
        icon: 'pi pi-clock',
      },
      {
        type: FinancialMetricType.RETURNED_CHECKS,
        label: "מספר צ'קים שחזרו",
        value: data?.financialMeasures.rejectedChecks.length ?? 0,
        icon: 'pi pi-times-circle',
      },
      {
        type: FinancialMetricType.PENDING_HOUR_CHANGE_REQUESTS,
        label: 'בקשות לשינוי במכסת שעות בקורס',
        value: data?.financialMeasures.pendingHourChangeRequests.length ?? 0,
        icon: 'pi pi-clock',
      },
      {
        type: FinancialMetricType.MONTHLY_HOURS,
        label: 'מספר שעות החודש',
        value: Math.round((data?.financialMeasures.totalHours || 0) * 10) / 10,
        icon: 'pi pi-calendar',
      },
      {
        type: FinancialMetricType.AVERAGE_HOURLY_RATE,
        label: 'תעריף ממוצע לשעת הוראה החודש',
        value:
          Math.round((data?.financialMeasures.averageCostPerHour || 0) * 10) /
          10,
        icon: 'pi pi-dollar',
      },
      {
        type: FinancialMetricType.TOTAL_TEACHING_EXPENSES,
        label: 'סה"כ הוצאות הוראה מתוכננות החודש',
        value:
          Math.round(
            (data?.financialMeasures.totalHours ?? 0) *
              (data?.financialMeasures.averageCostPerHour ?? 0) *
              10,
          ) / 10,
        icon: 'pi pi-wallet',
      },
    ]);
  }, [data]);

  const [showRatesDialog, setShowRatesDialog] = useState(false);
  const [showRejectedChecksDialog, setShowRejectedChecksDialog] =
    useState(false);
  const [showHourChangeRequestsDialog, setShowHourChangeRequestsDialog] =
    useState(false);

  // Format currency
  const formatCurrency = (value: number) => {
    return '₪' + value.toLocaleString();
  };

  // Handle metric click
  const handleMetricClick = (metricType: FinancialMetricType) => {
    switch (metricType) {
      case FinancialMetricType.PENDING_RATES:
        setShowRatesDialog(true);
        break;
      case FinancialMetricType.RETURNED_CHECKS:
        setShowRejectedChecksDialog(true);
        break;
      case FinancialMetricType.PENDING_HOUR_CHANGE_REQUESTS:
        setShowHourChangeRequestsDialog(true);
        break;
      default:
        console.log('Clicked metric:', metricType);
    }
  };

  return (
    <>
      <div className="metrics-grid">
        {financialMetrics.map((metric, index) => (
          <div
            key={index}
            style={{
              height: '160px',
              width: '100%',
              cursor: 'pointer',
              paddingTop: '16px',
            }}
            className={`custom-metric-card metric-type-${metric.icon.replace('pi pi-', '')}`}
            onClick={() => handleMetricClick(metric.type)}
          >
            <div className="metric-content">
              <div className="metric-icon-wrapper">
                <i className={metric.icon}></i>
              </div>
              <div className="metric-value">
                {metric.label === 'תעריף ממוצע לשעת הוראה החודש' ||
                metric.label === 'סה"כ הוצאות הוראה מתוכננות'
                  ? formatCurrency(metric.value)
                  : metric.value.toLocaleString()}
              </div>
              <div style={{ fontSize: '16px', color: '#64748b' }}>
                {metric.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Enhanced Rates Dialog */}
      <Dialog
        header="מעקב תעריפים - דשבורד מנהלים"
        visible={showRatesDialog}
        style={{ width: '95vw', maxWidth: '1400px' }}
        onHide={() => setShowRatesDialog(false)}
        maximizable
        modal
      >
        <RatesStatuses
          rates={data?.financialMeasures?.pendingRates || []}
          onRefresh={onDataRefresh}
        />
      </Dialog>

      {/* Rejected Checks Dialog */}
      <RejectedChecksDialog
        visible={showRejectedChecksDialog}
        onHide={() => setShowRejectedChecksDialog(false)}
        rejectedChecks={data?.financialMeasures?.rejectedChecks || []}
      />

      {/* Hour Change Requests Dialog */}
      <HourChangeRequestsDialog
        visible={showHourChangeRequestsDialog}
        onHide={() => setShowHourChangeRequestsDialog(false)}
        hourChangeRequests={
          data?.financialMeasures?.pendingHourChangeRequests || []
        }
        onDataRefresh={() => {
          if (onDataRefresh) {
            onDataRefresh();
            setShowHourChangeRequestsDialog(false);
          }
        }}
      />
    </>
  );
};
